import type { ReactNode } from "react"
import { useCallback, useMemo, useState } from "react"

import { useCabinetAccess } from "../../../access/use-access"
import { fetchJournal } from "../../../api/journal.api"
import { formatDateLong, shiftDate, todayInZone } from "../../../lib/format"
import { resolveBranch } from "../../../shell/use-branch"
import { Button } from "../../../ui"
import { useResource } from "../use-resource"
import styles from "./onboarding.module.css"

/**
 * 4-А6 «Страница готова».
 *
 * ГЛАВНОЕ ЗДЕСЬ — НЕ QR, А ПРЕДУПРЕЖДЕНИЕ. «Страница открывается, но свободного времени
 * на ней нет» — самый частый способ потерять первых клиентов: ссылка разослана,
 * запись включена, а график не заполнен, и человек видит пустой календарь. Признак
 * считается из журнала: докуда вообще заведены смены.
 *
 * ЧЕГО НЕТ. Самой картинки QR: рисовать её нечем — библиотеки кодирования в кабинете нет,
 * а маршрута «дай QR страницы записи» в API тоже. Поэтому на её месте стоит честная
 * рамка, а ссылка рядом — настоящая и копируется. «Отправить себе в мессенджер» тоже
 * не выдумывается: маршрута нет.
 */

/** На сколько дней вперёд смотрим график. Верхняя граница периода журнала — 31 день. */
const HORIZON_DAYS = 31

/** Меньше недели заполненного графика — уже повод предупредить. */
const ENOUGH_DAYS = 7

export function PageReadyScreen(props: { readonly onOpenSchedule: () => void }): ReactNode {
  const { context } = useCabinetAccess()
  const branch = resolveBranch(context, null)
  const timeZone = branch?.timezone ?? context.tenant.defaultTimezone
  const today = todayInZone(timeZone)

  const [copied, setCopied] = useState(false)

  const bookingUrl = `${context.tenant.slug}.planerius.ru`

  const journalQuery = useMemo(
    () =>
      branch === null
        ? null
        : {
            branchId: branch.id,
            dateFrom: today,
            dateTo: shiftDate(today, HORIZON_DAYS - 1),
            includeCancelled: false,
          },
    [branch, today],
  )

  const schedule = useResource(
    journalQuery === null ? null : (signal) => fetchJournal(journalQuery, signal),
    journalQuery === null ? null : `ready|${journalQuery.branchId}|${today}`,
  )

  /** Докуда заведены смены. null — не заведены вовсе. */
  const lastShiftDate = useMemo(() => {
    const workdays = schedule.data?.workdays ?? []
    if (workdays.length === 0) return null
    return workdays.reduce((max, workday) => (workday.date > max ? workday.date : max), workdays[0]?.date ?? today)
  }, [schedule.data, today])

  const enoughUntil = shiftDate(today, ENOUGH_DAYS)
  const warn = schedule.data !== null && (lastShiftDate === null || lastShiftDate < enoughUntil)

  const copy = useCallback(() => {
    const run = async (): Promise<void> => {
      try {
        await navigator.clipboard?.writeText(`https://${bookingUrl}`)
        setCopied(true)
      } catch {
        // Буфер обмена может быть закрыт настройками браузера. Молчать нельзя:
        // человек нажал и ждёт, что ссылка скопирована, — а её там нет.
        setCopied(false)
      }
    }
    void run()
  }, [bookingUrl])

  return (
    <div className={styles.page}>
      <div className={styles.column}>
        <div>
          <h1 className={styles.heading}>Страница записи работает</h1>
          <p className={styles.subheading}>
            Защищённое соединение включено сразу — ждать нечего.
          </p>
        </div>

        {warn ? (
          <div className={styles.warnPlate} role="status">
            <b>Страница открывается, но свободного времени на ней нет:</b>{" "}
            {lastShiftDate === null
              ? "у мастеров не заполнен график ни на один день."
              : `у мастеров не заполнен график дальше ${formatDateLong(lastShiftDate, timeZone)}.`}{" "}
            Заполните график — и запись заработает.{" "}
            <button type="button" className={styles.stepAction} onClick={props.onOpenSchedule}>
              К графику
            </button>
          </div>
        ) : null}

        <div className={styles.linkRow}>
          <span className={styles.linkText}>{bookingUrl}</span>
          <Button size="sm" onClick={copy}>
            {copied ? "Скопировано" : "Копировать"}
          </Button>
        </div>

        <div className={styles.qr}>
          <span>QR-код страницы записи</span>
          <span>появится, когда сервер начнёт его отдавать</span>
        </div>

        <div className={styles.pair}>
          <Button deniedReason="QR-кода пока нет — его не отдаёт сервер, а рисовать его в браузере нечем">
            Скачать QR
          </Button>
          <Button
            onClick={() => {
              window.open(`https://${bookingUrl}`, "_blank", "noopener")
            }}
          >
            Предпросмотр
          </Button>
        </div>

        <Button
          variant="primary"
          block
          deniedReason="Отправка ссылки в мессенджер появится вместе с подключением каналов"
        >
          Отправить себе в мессенджер
        </Button>

        <p className={styles.hint}>
          Проверьте с телефона клиента: откройте ссылку и попробуйте записаться к себе.
          Заготовки для соцсетей — в разделе «Каналы».
        </p>
      </div>
    </div>
  )
}
